import gsap from 'gsap';
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll';
import { getKeyboardFocusableElements, trapFocus } from '../utils.js';
import BaseComponent from './BaseComponent.js';

export default class Menu extends BaseComponent {
	constructor() {
		super();

		this.element = document.querySelector('[data-menu]');

		if (!this.element) {
			return;
		}

		this.bg = this.element.querySelector('[data-bg]');
		this.logo = this.element.querySelector('[data-logo]');
		this.menuToggle = this.element.querySelector('[data-menu-toggle]');
		this.content = this.element.querySelector('[data-replace-content]');
		this.pillars = this.content.querySelectorAll('.c-menu__primary-menu li');
		this.menuItems = this.content.querySelectorAll('.c-menu__secondary-menu li');

		// Utils
		this.breakpoint = window.matchMedia('(min-width: 992px)');
		this.parser = new DOMParser();

		// State
		this.isOpen = false;
		this.firstLoad = true;
		this.trapFocus = null;
		this.lastScrollTop = 0;
		this.isNavigating = false;

		// Events
		this.menuToggle.addEventListener('click', (e) => {
			this.isOpen ? this.close() : this.open();
		});

		document.addEventListener('leave', (e) => {
			this.isNavigating = true;
			this.close();
			this.element.classList.remove('is-hidden');
			this.lastScrollTop = 0;
		});

		document.addEventListener('after', (e) => {
			// Oude iOS versie hack :(
			setTimeout(() => {
				this.isNavigating = false;
			}, 100);
		});

		document.addEventListener('keydown', (event) => {
			if (event.key === 'Escape') {
				this.close();
			}
		});

		this.breakpoint.addEventListener('change', (e) => {
			if (e.matches) {
				this.reset();
				gsap.set([this.content, this.bg, this.pillars, this.menuItems], { clearProps: 'all' });
			}
		});

		window.addEventListener('scroll', this.checkScroll.bind(this), { passive: true });

		document.addEventListener('bless', (e) => {
			if (this.firstLoad) {
				this.firstLoad = false;
				return;
			}

			this.updateContent(e.detail?.next?.html);
		});
	}

	checkScroll() {
		if (this.isNavigating) {
			return;
		}

		const currentScrollTop = window.scrollY || document.documentElement.scrollTop;

		if (currentScrollTop > this.lastScrollTop && currentScrollTop > 60) {
			this.element.classList.add('is-hidden');
		} else {
			this.element.classList.remove('is-hidden');
		}

		this.lastScrollTop = currentScrollTop <= 0 ? 0 : currentScrollTop;
	}

	open() {
		if (this.isOpen) {
			return;
		}

		const tl = gsap.timeline();
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		this.isOpen = true;
		this.element.classList.add('is-open');
		this.menuToggle.setAttribute('aria-expanded', 'true');

		this.trapMenu();
		disablePageScroll();

		gsap.set(this.content, {
			y: () => {
				const contentHeight = this.content.offsetHeight;
				return -contentHeight - 64;
			},
			autoAlpha: 1,
		});

		gsap.set(this.pillars, {
			y: 10,
			autoAlpha: 0,
		});

		gsap.set(this.menuItems, {
			autoAlpha: 0,
		});

		tl.to(this.bg, {
			autoAlpha: 1,
			duration: prefersReducedMotion ? 0 : 0.6,
			ease: 'none',
		});

		tl.to(
			this.content,
			{
				y: '0%',
				duration: prefersReducedMotion ? 0 : 0.6,
				ease: 'power3.out',
			},
			'<'
		);

		tl.to(
			this.pillars,
			{
				y: 0,
				autoAlpha: 1,
				stagger: prefersReducedMotion ? 0 : 0.1,
				duration: prefersReducedMotion ? 0 : 0.8,
				ease: 'power4.out',
			},
			'<+=0.25'
		);

		tl.to(
			this.menuItems,
			{
				autoAlpha: 1,
				duration: prefersReducedMotion ? 0 : 0.6,
				ease: 'none',
			},
			'<+=0.25'
		);
	}

	close() {
		if (!this.isOpen) {
			return;
		}

		this.reset();

		const tl = gsap.timeline();
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		tl.to([this.pillars, this.menuItems], {
			autoAlpha: 0,
			duration: prefersReducedMotion ? 0 : 0.3,
			ease: 'none',
		});

		tl.to(
			this.content,
			{
				y: () => {
					const contentHeight = this.content.offsetHeight;
					return -contentHeight - 64;
				},
				duration: prefersReducedMotion ? 0 : 0.6,
				ease: 'power3.out',
			},
			'<+=0.25'
		);

		tl.to(
			this.bg,
			{
				autoAlpha: 0,
				duration: prefersReducedMotion ? 0 : 0.6,
				ease: 'none',
			},
			'<+=0.25'
		);
	}

	reset() {
		this.isOpen = false;
		this.element.classList.remove('is-open');
		this.menuToggle.setAttribute('aria-expanded', 'false');
		this.element.classList.remove('is-hidden');

		this.trapFocus?.remove();
		this.trapFocus = null;

		enablePageScroll();
	}

	trapMenu() {
		this.trapFocus = trapFocus(this.element, [
			this.menuToggle,
			...getKeyboardFocusableElements(this.content),
			this.logo,
		]);
	}

	updateContent(html) {
		if (!html) {
			return;
		}

		const parsed = this.parser.parseFromString(html, 'text/html');
		const newMenu = parsed.querySelector('[data-menu] [data-replace-content]');

		if (!newMenu) {
			return;
		}

		// Update pillars
		const newPillarLinks = newMenu.querySelectorAll('.c-menu__primary-menu li a');
		const oldPillarLinks = this.content.querySelectorAll('.c-menu__primary-menu li a');

		oldPillarLinks.forEach((link) => {
			link.classList.remove('is-active');
		});

		newPillarLinks.forEach((link) => {
			const href = link.getAttribute('href');
			const newLinkActive = link.classList.contains('is-active');
			const currentLink = [...oldPillarLinks].find((oldLink) => oldLink.getAttribute('href') === href);

			if (currentLink && newLinkActive) {
				currentLink.classList.add('is-active');
			}
		});

		// Update menu items
		const newMenuItems = newMenu.querySelectorAll('.c-menu__secondary-menu li');

		this.menuItems.forEach((item) => {
			item.classList.remove('current-menu-item', 'current_page_item');
		});

		newMenuItems.forEach((item) => {
			const href = item.querySelector('a').getAttribute('href');
			const newLinkActive =
				item.classList.contains('current-menu-item') || item.classList.contains('current_page_item');
			const currentLink = [...this.menuItems].find(
				(oldLink) => oldLink.querySelector('a').getAttribute('href') === href
			);

			if (currentLink && newLinkActive) {
				currentLink.classList.add('current-menu-item', 'current_page_item');
			}
		});
	}
}
